import { useMutation } from '@apollo/client'
import { Input, message } from 'antd'
import { Rule, RuleObject } from 'antd/lib/form'
import React, { useCallback, useState } from 'react'
import { GET_MOBILE_CODE, SET_PWD_BY_MOBILE } from '~/graphqls/gqls'
import { ValidInfoProps, Captcha } from '~/components/pages/login/Captcha'
import CryptoJS from 'crypto-js'
import { MyForm } from '~/components/ui/MyForm'
import { MyModal } from '~/components/ui/MyModal'
import { graphQLErrorMessage, showMobile } from '~/utils'
import { MyBtn } from '~/components/ui/MyBtn'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react'

interface SetPwdModalProps {
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
}

export const SetPwdModal: React.FC<SetPwdModalProps> = observer(
  ({ visible, setVisible }) => {
    const [form] = MyForm.useForm()
    const { store } = useStores()

    const [loading, setLoading] = useState(false)
    const [passwordStatus, setPasswordStatus] = useState(0)

    const [getMobileCodeAction] = useMutation(GET_MOBILE_CODE)
    const [setPwdByMobileAction] = useMutation(SET_PWD_BY_MOBILE)

    const confirmPwd = useCallback(
      (_rule: RuleObject, value: string) => {
        const pwd = form.getFieldValue('password')
        if (!value) {
          return Promise.reject('请再次输入密码')
        } else if (value !== pwd) {
          return Promise.reject('两次密码输入不一致')
        } else {
          return Promise.resolve()
        }
      },
      [form]
    )
    const initialValues = {
      mobile: showMobile(store.userinfo?.base?.mobile),
    }

    const getMobileCode = useCallback(
      (validInfo: ValidInfoProps) => {
        getMobileCodeAction({
          variables: {
            mobile: store.userinfo?.base?.mobile as string,
            scene: 'CHANGE_PWD',
            behaviorParam: {
              token: validInfo.token,
              authenticate: validInfo.authenticate,
            },
            areaCode: `+${store.userinfo?.areaCode}`,
          },
        })
          .then(() => {
            message.destroy()
            message.success('获取验证码成功')
          })
          .catch(error => {
            message.destroy()
            message.error(graphQLErrorMessage(error) || '获取验证码失败')
          })
      },
      [form, store.userinfo, getMobileCodeAction]
    )

    const onConfirm = useCallback(async () => {
      const formData = await form.validateFields()

      setLoading(true)

      setPwdByMobileAction({
        variables: {
          mobile: store.userinfo?.base?.mobile as string,
          code: formData.code,
          password: CryptoJS.MD5(formData.password).toString(),
          areaCode: `+${store.userinfo?.areaCode}`,
        },
      })
        .then(() => {
          message.destroy()
          message.success('设置密码成功')
          setVisible(false)
        })
        .catch(error => {
          message.destroy()
          message.error(graphQLErrorMessage(error) || '设置密码失败')
        })
        .finally(() => {
          setLoading(false)
        })
    }, [form, store.userinfo, setPwdByMobileAction])

    const validatePassword = (rule: Rule, value: string) => {
      const regex =
        /^(?![a-zA-Z]+$)(?![A-Z0-9]+$)(?![A-Z\W_]+$)(?![a-z0-9]+$)(?![a-z\W_]+$)(?![0-9\W_]+$)[a-zA-Z0-9\W_]{8,}$/
      if (!regex.test(value)) {
        setPasswordStatus(-1)
        if (value.trim() === '') {
          return Promise.reject('')
        } else {
          return Promise.reject(
            '密码必须至少包含8位，并包含大小写字母、数字和符号中的3种'
          )
        }
      } else {
        setPasswordStatus(1)
        return Promise.resolve()
      }
    }

    return (
      <>
        <MyModal
          dark
          visible={visible}
          title="设置密码"
          onCancel={() => setVisible(false)}
          maskStyle={{
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}
          width={448}
          footer={null}
        >
          <MyForm form={form} initialValues={initialValues} $dark>
            <MyForm.Item
              name="mobile"
              rules={[
                {
                  required: true,
                  message: '请输入手机号',
                },
              ]}
            >
              <Input
                placeholder="手机号"
                disabled
                addonBefore={`+${store.userinfo?.areaCode}`}
              />
            </MyForm.Item>
            <MyForm.Item
              name="code"
              rules={[{ required: true, message: '请输入手机验证码' }]}
            >
              <Input
                placeholder="手机验证码"
                addonAfter={
                  <Captcha
                    captchaId="get-pwd-captcha"
                    onSuccess={getMobileCode}
                  />
                }
              />
            </MyForm.Item>
            <MyForm.Item
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { validator: validatePassword },
              ]}
              validateStatus={
                passwordStatus == 1
                  ? 'success'
                  : passwordStatus == -1
                  ? 'error'
                  : ''
              }
            >
              <Input.Password placeholder="密码" />
            </MyForm.Item>
            <MyForm.Item
              name="checkPassword"
              rules={[
                {
                  validator: confirmPwd,
                },
              ]}
            >
              <Input.Password placeholder="确认密码" />
            </MyForm.Item>
            <MyBtn
              onClick={onConfirm}
              style={{ marginBottom: 16 }}
              type="primary"
              size="large"
              loading={loading}
              block
            >
              立即设置
            </MyBtn>
            <MyBtn onClick={() => setVisible(false)} type="link" block>
              取消设置密码
            </MyBtn>
          </MyForm>
        </MyModal>
      </>
    )
  }
)
